import { Drawer as AntDrawer } from 'antd'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { MdCall, MdOutlineNotifications, MdOutlineReportProblem, MdOutlineRoute } from 'react-icons/md'

import { restClient } from '@/Core'
import { shiftLabel } from '@/Containers/AssignmentSection/AssignmentContainer/Helpers/shiftField'
import { formatAppDate } from '@/Ship/Helpers/appTime'
import { cx } from '@/Ship/Helpers/cssClass'
import { offlineSyncManager } from '@/Ship/Pwa/OfflineSync'
import { pwaManager } from '@/Ship/Pwa/PwaManager'

import { myShiftStore, shiftCovers } from '../../Stores/MyShiftStore'
import { RouteStrip } from '../RouteStrip/RouteStrip'
import { ConfirmStatusSheet } from '../TaskCard/ConfirmStatusSheet'
import { TaskActionButtons } from '../TaskCard/TaskActionButtons'
import { TASK_ACTIONS, TASK_STATUS_COLORS, type TaskAction } from '../TaskCard/taskStatus'
import { AssignmentDetailDrawerStore } from './AssignmentDetailDrawerStore'
import { CompleteTaskModal } from './CompleteTaskModal'
import { ACTIVE_EXECUTION_STATUSES, STATUS_LABELS } from './Constants'
import { DelayModal } from './DelayModal'
import { DelaysSection } from './DelaysSection'
import {
    getCargo,
    getCreatedAt,
    getCustomerName,
    getCustomerPhone,
    getDispatcherName,
    getDispatcherPhone,
    getLoadingPoint,
    getRequestStatus,
    getRouteUrl,
    getUnloadingPoint,
    isRequestFrozen,
} from './Helpers'
import { QRSection } from './QRSection'

import styles from './Drawer.module.css'

import type { AssignmentItem } from './Types'

interface AssignmentDetailDrawerProps {
    assignment: AssignmentItem | null
    open: boolean
    onClose: () => void
    onStatusChanged: (uuid: string, newStatus: string, shift?: AssignmentItem['shift']) => void
    onViewNotifications?: () => void
}

/** Велика кнопка дзвінка: ім'я, номер і зелена слухавка; без номера — просто рядок */
const Contact = ({ label, name, phone }: { label: string; name: string | null; phone: string | null }) => {
    const inner = (
        <>
            <div className={cx(styles, 'contactMain')}>
                <span className="pwa-label">{label}</span>
                <span className={`${cx(styles, 'contactName')}${name ? '' : ` ${cx(styles, 'contactName--empty')}`}`}>
                    {name || 'Не вказано'}
                </span>
                {phone && <span className={cx(styles, 'contactPhone')}>{phone}</span>}
            </div>
            {phone && (
                <span className={cx(styles, 'callIcon')} aria-hidden="true">
                    <MdCall />
                </span>
            )}
        </>
    )
    return phone ? (
        <a className={cx(styles, 'contact')} href={`tel:${phone}`} aria-label={`Зателефонувати: ${label}`}>
            {inner}
        </a>
    ) : (
        <div className={cx(styles, 'contact')}>{inner}</div>
    )
}

/**
 * Деталі завдання: шапка з номером і статусом, маршрут, час, контакти-дзвінки,
 * документи (е-ТТН, QR), коментар, затримки. Дії — у закріпленій панелі знизу.
 */
export const AssignmentDetailDrawer = observer(
    ({ assignment, open, onClose, onStatusChanged, onViewNotifications }: AssignmentDetailDrawerProps) => {
        const [store] = useState(() => new AssignmentDetailDrawerStore())

        // Скидаємо помилку, коли статус змінився ззовні
        // Дія, що чекає підтвердження водія у шторці
        const [pending, setPending] = useState<TaskAction | null>(null)

        useEffect(() => {
            store.setActionError(null)
            setPending(null)
        }, [assignment?.status])

        const handleStatusChange = useCallback(
            async (status: string, confirmedAt: string) => {
                if (!assignment) return

                // Офлайн: оптимістичне оновлення + мутація в чергу
                if (pwaManager.isOffline) {
                    try {
                        await offlineSyncManager.enqueue({
                            type: 'status-change',
                            uuid: assignment.uuid,
                            newStatus: status,
                            confirmedAt,
                        })
                        onStatusChanged(assignment.uuid, status)
                    } catch {
                        store.setActionError('Не вдалось зберегти офлайн. Спробуйте ще раз.')
                    }
                    return
                }

                store.setActionLoading(true)
                store.setActionError(null)
                try {
                    const updated = (await restClient.post(
                        `/services/executor-assignments/${assignment.uuid}/change-status`,
                        { json: { status, confirmedAt } }
                    )) as unknown as Partial<AssignmentItem> | null
                    onStatusChanged(assignment.uuid, status, updated?.shift)
                } catch (err: any) {
                    console.error('[AssignmentDetailDrawer] change-status error', err)
                    store.setActionError(
                        err?.response?.message || err?.response?.detail || err?.message || 'Не вдалося змінити статус'
                    )
                } finally {
                    store.setActionLoading(false)
                }
            },
            [assignment, onStatusChanged]
        )

        const a = assignment
        if (!a) return null

        const cargo = getCargo(a)
        const loadingPoint = getLoadingPoint(a)
        const unloadingPoint = getUnloadingPoint(a)
        const planned = formatAppDate(a.plannedLoadingDate, 'DD.MM, HH:mm')
        const created = formatAppDate(getCreatedAt(a), 'DD.MM.YYYY, HH:mm')
        const shift = shiftLabel(a.shift)
        const routeUrl = getRouteUrl(a)
        const statusLabel = STATUS_LABELS[a.status] || a.status
        const statusColor = TASK_STATUS_COLORS[a.status] || '#8c8c8c'
        const isFinished = a.status === 'completed_by_driver' || a.status === 'completed_by_dispatcher'
        // Скасована/завершена заявка заморожує завдання
        const requestFrozen = isRequestFrozen(a)
        const frozenLabel = getRequestStatus(a) === 'completed' ? 'Заявку завершено' : 'Заявку скасовано'
        // Переглянути можна будь-яке завдання (напр. зі сповіщення), а діяти — лише в межах активної зміни
        const shiftBlock = requestFrozen || isFinished ? null : myShiftStore.taskActionBlockReason(a)
        const isCurrent = ACTIVE_EXECUTION_STATUSES.includes(a.status) && shiftCovers(myShiftStore.active, a)
        const canDelay = !requestFrozen && !shiftBlock && ACTIVE_EXECUTION_STATUSES.includes(a.status)
        const busy = store.actionLoading

        const actions = requestFrozen || shiftBlock || isFinished ? [] : (TASK_ACTIONS[a.status] ?? [])
        // Завершення підтверджується в поп-апі оцінок, решта — у шторці підтвердження
        const runAction = (action: TaskAction) => {
            if (action.next === 'completed_by_driver') {
                store.setCompleteTaskOpen(true)
                return
            }
            setPending(action)
        }

        const title = (
            <div className={cx(styles, 'header')}>
                <div className={cx(styles, 'headerText')}>
                    <span className={cx(styles, 'headerEyebrow')}>Завдання</span>
                    <span className={cx(styles, 'headerNumber')}>
                        <small>#</small>
                        {a.id}
                    </span>
                </div>
                <span
                    className={`pwa-status pwa-status--ellipsis ${cx(styles, 'headerStatus')}`}
                    style={{ '--status': statusColor } as CSSProperties}
                >
                    {statusLabel}
                </span>
            </div>
        )

        const footer = (
            <div className={cx(styles, 'footer')}>
                {store.actionError && <div className="pwa-note pwa-note--danger">{store.actionError}</div>}
                {requestFrozen && (
                    <div className="pwa-note pwa-note--danger">{frozenLabel} — виконання завдання недоступне.</div>
                )}
                {!requestFrozen && shiftBlock && (
                    <div className="pwa-note pwa-note--warn" data-testid="shift-block">
                        {shiftBlock}
                    </div>
                )}
                <TaskActionButtons actions={actions} loading={busy} onRun={runAction} />
                {routeUrl && (
                    <a
                        className={`pwa-btn ${actions.length > 0 ? 'pwa-btn--ghost pwa-btn--compact' : 'pwa-btn--secondary'} ${cx(styles, 'route')}`}
                        href={routeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <MdOutlineRoute /> Маршрут
                    </a>
                )}
            </div>
        )

        return (
            <>
                <AntDrawer
                    open={open}
                    onClose={onClose}
                    placement="bottom"
                    height="94vh"
                    closable
                    title={title}
                    footer={footer}
                    styles={{
                        header: { background: 'var(--pwa-bar)', borderBottom: '1px solid var(--pwa-line)' },
                        body: { padding: 0, background: 'var(--pwa-bg)' },
                        footer: { padding: 0, border: 'none' },
                        wrapper: { background: 'var(--pwa-bg)' },
                    }}
                >
                    <div className={cx(styles, 'body')}>
                        <section className={cx(styles, 'section')}>
                            <h3 className={`${cx(styles, 'cargo')}${cargo ? '' : ` ${cx(styles, 'cargo--empty')}`}`}>
                                {cargo || 'Вантаж не вказано'}
                            </h3>
                            <RouteStrip
                                from={loadingPoint}
                                to={unloadingPoint}
                                tone={isCurrent ? 'marking' : 'default'}
                                size="lg"
                            />
                        </section>

                        <section className={cx(styles, 'section')}>
                            <div className={cx(styles, 'when')}>
                                <div className={cx(styles, 'whenItem')}>
                                    <span className="pwa-label">Планове завантаження</span>
                                    <span className={cx(styles, 'whenBig')}>{planned ?? '—'}</span>
                                </div>
                                <div className={cx(styles, 'whenItem')}>
                                    <span className="pwa-label">Створено</span>
                                    <span className="pwa-value">{created ?? '—'}</span>
                                </div>
                                {shift && (
                                    <div className={`${cx(styles, 'whenItem')} ${cx(styles, 'whenItem--wide')}`}>
                                        <span className="pwa-label">Зміна</span>
                                        <span className="pwa-value">{shift}</span>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className={cx(styles, 'section')}>
                            <h4 className={cx(styles, 'sectionTitle')}>Контакти</h4>
                            <Contact label="Диспетчер" name={getDispatcherName(a)} phone={getDispatcherPhone(a)} />
                            <Contact
                                label="Відповідальний замовник"
                                name={getCustomerName(a)}
                                phone={getCustomerPhone(a)}
                            />
                        </section>

                        <section className={cx(styles, 'section')}>
                            <h4 className={cx(styles, 'sectionTitle')}>Документи</h4>
                            <div className={cx(styles, 'row')}>
                                <span className={cx(styles, 'rowLabel')}>е-ТТН</span>
                                <span className={cx(styles, 'rowHint')}>З'явиться в наступних версіях</span>
                            </div>
                            <QRSection assignment={a} />
                        </section>

                        <section className={cx(styles, 'section')}>
                            <h4 className={cx(styles, 'sectionTitle')}>Коментар диспетчера</h4>
                            <div
                                className={`${cx(styles, 'comment')}${a.dispatcherComment ? '' : ` ${cx(styles, 'comment--empty')}`}`}
                            >
                                {a.dispatcherComment || 'Без коментаря'}
                            </div>
                        </section>

                        <section className={cx(styles, 'section')}>
                            <h4 className={cx(styles, 'sectionTitle')}>Затримки</h4>
                            {canDelay && (
                                <button
                                    type="button"
                                    className="pwa-btn pwa-btn--danger pwa-btn--block"
                                    onClick={() => store.setDelayModalOpen(true)}
                                >
                                    <MdOutlineReportProblem /> Повідомити про затримку
                                </button>
                            )}
                            <DelaysSection assignmentUuid={a.uuid} refreshKey={store.delayRefreshKey} />
                        </section>

                        <button
                            type="button"
                            className="pwa-btn pwa-btn--ghost pwa-btn--block"
                            onClick={onViewNotifications}
                        >
                            <MdOutlineNotifications /> Сповіщення щодо завдання
                        </button>
                    </div>
                </AntDrawer>

                <ConfirmStatusSheet
                    action={pending}
                    taskId={a.id}
                    currentStatus={a.status}
                    onConfirm={(action, confirmedAt) => {
                        setPending(null)
                        handleStatusChange(action.next, confirmedAt)
                    }}
                    onCancel={() => setPending(null)}
                />

                <DelayModal
                    open={store.delayModalOpen}
                    onClose={() => store.setDelayModalOpen(false)}
                    assignmentUuid={a.uuid}
                    onReported={() => store.bumpDelayRefreshKey()}
                />

                <CompleteTaskModal
                    open={store.completeTaskOpen}
                    onClose={() => store.setCompleteTaskOpen(false)}
                    assignmentUuid={a.uuid}
                    plannedLoadingDate={a.plannedLoadingDate}
                    onCompleted={(uuid, newStatus) => {
                        onStatusChanged(uuid, newStatus)
                        onClose()
                    }}
                />
            </>
        )
    }
)
